/**
 * ProjectProductsTab - Manage products assigned to a project
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  TextField,
  Autocomplete,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import projectsService, { ProjectProduct } from '../services/projects';
import productsService, { ProductListItem } from '../services/products';
import { useAuth } from '../context/AuthContext';
import { isSuperAdmin } from '../utils/userPermissions';
import { collapseProductRevisions } from '../utils/products';
import { useCompanyStrengthUnitLookup } from '../utils/useCompanyStrengthUnit';
import { valueWithUnit } from '../utils/maturityDisplay';

interface ProjectProductsTabProps {
  projectId: string;
  /** Company the project belongs to — scopes the assignable list for super admins */
  companyId?: string;
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
}

const ProjectProductsTab: React.FC<ProjectProductsTabProps> = ({
  projectId,
  companyId,
  onError,
  onSuccess,
}) => {
  const { user: currentUser } = useAuth();
  const [products, setProducts] = useState<ProjectProduct[]>([]);
  // A grade is in its mix design company's strength unit (StrengthFormat),
  // as on the mix design and pour setup pages: a supplier's mix design can
  // be assigned to another company's project. Older responses without the
  // mix design's company fall back to the project's. No unit is shown until
  // it is known.
  const gradeCompanyId = (p: ProjectProduct): string | null | undefined =>
    p.company_id !== undefined ? p.company_id : companyId || currentUser?.CompanyID;
  const strengthUnitOf = useCompanyStrengthUnitLookup(
    products.filter((p) => p.strength_grade).map(gradeCompanyId),
  );
  const [loading, setLoading] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<string | null>(null);
  const [selectedProductId, setSelectedProductId] = useState('');

  // Assignable mix designs, loaded when the dialog first opens
  const [availableProducts, setAvailableProducts] = useState<ProductListItem[] | null>(null);
  useEffect(() => {
    if (openAddDialog && availableProducts === null) {
      productsService
        .listProducts(isSuperAdmin(currentUser) && companyId ? companyId : undefined)
        .then(setAvailableProducts)
        .catch((err: any) => {
          setAvailableProducts([]);
          onError(err.response?.data?.detail || 'Failed to load mix designs');
        });
    }
  }, [openAddDialog, availableProducts, companyId, currentUser, onError]);

  const productLabel = (p: ProductListItem): string =>
    p.description ? `${p.code} - ${p.description}` : p.code;

  // Collapse per-revision rows to the newest revision, then hide mixes
  // already assigned to the project.
  const assignableProducts = useMemo(
    () =>
      collapseProductRevisions(availableProducts ?? [], productLabel).filter(
        (p) => !products.some((assigned) => assigned.product_id === p.product_id),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [availableProducts, products],
  );

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const data = await projectsService.getProjectProducts(projectId);
      setProducts(data);
    } catch (err: any) {
      onError(err.response?.data?.detail || 'Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [projectId, onError]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const handleAddProduct = async () => {
    if (!selectedProductId) {
      onError('Please select a mix design');
      return;
    }
    
    setLoading(true);
    try {
      await projectsService.assignProduct(projectId, selectedProductId);
      onSuccess('Product assigned successfully');
      setOpenAddDialog(false);
      setSelectedProductId('');
      loadProducts();
    } catch (err: any) {
      onError(err.response?.data?.detail || 'Failed to assign product');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveClick = (productId: string) => {
    setProductToDelete(productId);
    setDeleteConfirmOpen(true);
  };

  const handleRemoveConfirm = async () => {
    if (!productToDelete) return;
    
    setLoading(true);
    try {
      await projectsService.removeProduct(projectId, productToDelete);
      onSuccess('Product removed successfully');
      setDeleteConfirmOpen(false);
      setProductToDelete(null);
      loadProducts();
    } catch (err: any) {
      onError(err.response?.data?.detail || 'Failed to remove product');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveCancel = () => {
    setDeleteConfirmOpen(false);
    setProductToDelete(null);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Mix Design &amp; Recipes</Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setOpenAddDialog(true)}
          disabled={loading}
        >
          Assign Mix Design
        </Button>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Strength Grade</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {products.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography color="text.secondary">
                    No products assigned yet
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              products.map((product) => (
                <TableRow key={product.product_id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {product.code}
                    </Typography>
                  </TableCell>
                  <TableCell>{product.description}</TableCell>
                  <TableCell>
                    {product.strength_grade ? (
                      <Chip
                        label={valueWithUnit(product.strength_grade, strengthUnitOf(gradeCompanyId(product)))}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveClick(product.product_id)}
                      disabled={loading}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Product Dialog */}
      <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Mix Design to Project</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Autocomplete
              fullWidth
              loading={availableProducts === null}
              options={assignableProducts}
              getOptionLabel={productLabel}
              isOptionEqualToValue={(option, val) => option.product_id === val.product_id}
              value={(availableProducts ?? []).find((p) => p.product_id === selectedProductId) || null}
              onChange={(_, selected) => setSelectedProductId(selected?.product_id || '')}
              renderInput={(params) => (
                <TextField {...params} label="Mix Design / Recipe" placeholder="Search mix designs..." />
              )}
              noOptionsText={
                availableProducts === null ? 'Loading mix designs...' : 'No mix designs available to assign'
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddDialog(false)}>Cancel</Button>
          <Button onClick={handleAddProduct} variant="contained" disabled={loading}>
            Assign
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={handleRemoveCancel}>
        <DialogTitle>Confirm Remove</DialogTitle>
        <DialogContent>
          Are you sure you want to remove this product from the project?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRemoveCancel}>Cancel</Button>
          <Button onClick={handleRemoveConfirm} color="error" variant="contained" disabled={loading}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectProductsTab;

